import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { ConnectVRChatComponent } from '../../dialog/connect-vrchat/connect-vrchat.component';

import { DisconnectVrchatComponent } from '../../dialog/disconnect-vrchat/disconnect-vrchat.component';
import { DisconnectPatreonComponent } from '../../dialog/disconnect-patreon/disconnect-patreon.component';
import { GuestSubmissionComponent } from '../../dialog/guest-submission/guest-submission.component';
import { AccountService } from '../../service/account.service';
import { ChatbotService } from '../../service/chatbot.service';

type TroubleshootActionId =
  | 'connect-vrchat'
  | 'disconnect-vrchat'
  | 'connect-patreon'
  | 'disconnect-patreon'
  | 'submit-script'
  | 'open-discord'
  | 'open-cultbot';

type TroubleshootVisibility =
  | 'vrchat-connected'
  | 'vrchat-disconnected'
  | 'patreon-connected'
  | 'patreon-disconnected';

// Checks that read the signed-in user's account instead of asking a question.
type TroubleshootAutoCheck = 'vrchat-connected-check' | 'patreon-active-check';

// Result text that's computed from live account data instead of being static copy.
type TroubleshootDynamicText = 'perks-vrchat-missing' | 'perks-patreon-issue';

interface TroubleshootAction {
  label: string;
  icon: string;
  action: TroubleshootActionId;
  visibleWhen?: TroubleshootVisibility;
}

interface TroubleshootOption {
  label: string;
  next: string;
}

interface TroubleshootNode {
  id: string;
  question: string;
  // Short explanation shown under the question, giving context before the user picks.
  hint?: string;
  options?: TroubleshootOption[];
  resultText?: string;
  actions?: TroubleshootAction[];
  // When set, this node is resolved automatically from account state instead
  // of being shown to the user.
  autoCheck?: TroubleshootAutoCheck;
  // Node reads account data (directly or via autoCheck/action visibility) —
  // requires the user to be logged in, otherwise resolves to the login wall.
  requiresAccount?: boolean;
  // Marks the dedicated "please log in" leaf.
  loginRequired?: boolean;
  // Overrides resultText with copy computed from the user's actual connection state.
  dynamicText?: TroubleshootDynamicText;
  // Shows a banner confirming we actually looked at the user's account.
  showAccountCheck?: boolean;
}

const LOGIN_REQUIRED_ID = 'login-required';

const TROUBLESHOOT_ROOT_ID = 'root';

@Component({
  selector: 'app-help',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatExpansionModule,
    MatTooltipModule
  ],
  templateUrl: './help.component.html',
  styleUrl: './help.component.less'
})
export class HelpComponent {

  // Guided troubleshooting tree: each node is either a question (with options
  // pointing at further node ids) or a result (with resolution text/actions).
  readonly troubleshootTree: TroubleshootNode[] = [
    {
      id: TROUBLESHOOT_ROOT_ID,
      question: 'What do you need help with?',
      hint: 'Pick whichever is closest to your situation and we\'ll ask a couple more quick questions, then take you straight to the fix or button you need — no need to dig through the FAQ.',
      options: [
        { label: 'Connecting or updating my accounts', next: 'connections' },
        { label: 'My in-game perks aren\'t working', next: 'perks-check-vrchat' },
        { label: 'Something else', next: 'other' },
      ]
    },
    {
      id: 'connections',
      question: 'What do you need to do with your accounts?',
      hint: 'Connecting VRChat links your in-world avatar to your Jar.games account; connecting Patreon verifies your membership. You need both connected to receive perks in-game.',
      options: [
        { label: 'Connect my VRChat account', next: 'result-connect-vrchat' },
        { label: 'Connect my Patreon account', next: 'result-connect-patreon' },
        { label: 'I changed my VRChat username', next: 'result-vrchat-renamed' },
        { label: 'Disconnect an account', next: 'result-disconnect' },
      ]
    },
    {
      // Not shown to the user — resolved from the signed-in account.
      id: 'perks-check-vrchat',
      question: 'Checking your VRChat connection…',
      autoCheck: 'vrchat-connected-check',
      requiresAccount: true,
    },
    {
      // Not shown to the user — resolved from the signed-in account.
      id: 'perks-check-patreon',
      question: 'Checking your Patreon connection…',
      autoCheck: 'patreon-active-check',
      requiresAccount: true,
    },
    {
      id: 'perks-check-restart',
      question: 'Have you fully restarted VRChat since both accounts were connected?',
      hint: 'Your VRChat and Patreon look good on our end, so this is almost always a timing issue. New connections are only picked up by the game in batches roughly every 15 minutes, and VRChat needs a full restart (not just switching worlds) to fetch the update.',
      showAccountCheck: true,
      options: [
        { label: 'Yes, and I waited a while', next: 'result-perks-still-broken' },
        { label: 'Not yet', next: 'result-restart-instructions' },
      ]
    },
    {
      id: 'other',
      question: 'What\'s going on?',
      hint: 'Not sure which category fits? Pick the closest match — CultBot and Discord can take it from there either way.',
      options: [
        { label: 'I found a bug on the website', next: 'result-website-bug' },
        { label: 'I have a billing / Patreon question', next: 'result-patreon-issue' },
        { label: 'None of these match my issue', next: 'result-fallback' },
      ]
    },

    // --- Results ---
    {
      // Dedicated to the perks auto-check — shows the account-check banner and
      // explains the specific reason, rather than the generic connect copy below.
      id: 'result-perks-no-vrchat',
      question: 'Connect your VRChat account',
      dynamicText: 'perks-vrchat-missing',
      requiresAccount: true,
      showAccountCheck: true,
      actions: [
        { label: 'Connect VRChat Account', icon: 'link', action: 'connect-vrchat' },
      ]
    },
    {
      // Dedicated to the perks auto-check — shows the account-check banner and
      // explains the specific reason, rather than the generic copy below.
      id: 'result-perks-patreon-issue',
      question: 'Fix your Patreon connection',
      dynamicText: 'perks-patreon-issue',
      requiresAccount: true,
      showAccountCheck: true,
      actions: [
        { label: 'Connect Patreon Account', icon: 'link', action: 'connect-patreon', visibleWhen: 'patreon-disconnected' },
        { label: 'Disconnect Patreon Account', icon: 'link_off', action: 'disconnect-patreon', visibleWhen: 'patreon-connected' },
      ]
    },
    {
      id: 'result-connect-vrchat',
      question: 'Connect your VRChat account',
      resultText: 'Provide your VRChat profile link or display name and we\'ll link it to your account for in-game perks.',
      requiresAccount: true,
      actions: [
        { label: 'Connect VRChat Account', icon: 'link', action: 'connect-vrchat', visibleWhen: 'vrchat-disconnected' },
        { label: 'Update VRChat Connection', icon: 'update', action: 'connect-vrchat', visibleWhen: 'vrchat-connected' },
      ]
    },
    {
      id: 'result-connect-patreon',
      question: 'Connect your Patreon account',
      resultText: 'You\'ll be redirected to Patreon to authorize the connection. Make sure you\'re logged into the Patreon account with your active pledge first — Patreon doesn\'t show which account is being linked during the process.',
      requiresAccount: true,
      actions: [
        { label: 'Connect Patreon Account', icon: 'link', action: 'connect-patreon', visibleWhen: 'patreon-disconnected' },
        { label: 'Disconnect Patreon Account', icon: 'link_off', action: 'disconnect-patreon', visibleWhen: 'patreon-connected' },
      ]
    },
    {
      id: 'result-vrchat-renamed',
      question: 'Update your VRChat connection',
      resultText: 'Your connection is tied to your VRChat account, not your display name, but if perks stop working after a rename, reconnect using your new profile link so we can find you correctly. Changes take up to 15 minutes to process.',
      actions: [
        { label: 'Update VRChat Connection', icon: 'update', action: 'connect-vrchat' },
      ]
    },
    {
      id: 'result-disconnect',
      question: 'Disconnect an account',
      resultText: 'You can disconnect your VRChat or Patreon account at any time. Disconnecting Patreon will remove your in-game perks until you reconnect.',
      requiresAccount: true,
      actions: [
        { label: 'Disconnect VRChat', icon: 'link_off', action: 'disconnect-vrchat', visibleWhen: 'vrchat-connected' },
        { label: 'Disconnect Patreon', icon: 'link_off', action: 'disconnect-patreon', visibleWhen: 'patreon-connected' },
      ]
    },
    {
      id: 'result-patreon-issue',
      question: 'Check your Patreon connection',
      resultText: 'If your status shows "No Membership" or "Payment Declined", double-check that you connected the Patreon account with the active pledge — Patreon doesn\'t indicate which account is linked during authorization. Reconnect if needed.',
      requiresAccount: true,
      actions: [
        { label: 'Connect Patreon Account', icon: 'link', action: 'connect-patreon', visibleWhen: 'patreon-disconnected' },
        { label: 'Disconnect Patreon Account', icon: 'link_off', action: 'disconnect-patreon', visibleWhen: 'patreon-connected' },
      ]
    },
    {
      id: 'result-restart-instructions',
      question: 'Restart VRChat to apply your perks',
      resultText: 'Connections are processed roughly every 15 minutes. Once both accounts show as connected, fully close VRChat (not just change worlds), wait about 15 minutes, then relaunch and rejoin the Audience Anarchy world.',
      actions: [
        { label: 'Ask CultBot', icon: 'smart_toy', action: 'open-cultbot' },
      ]
    },
    {
      id: 'result-perks-still-broken',
      question: 'Let\'s get you direct help',
      resultText: 'That\'s unusual — both accounts should be connected and processed by now. This looks like something the team needs to check directly.',
      actions: [
        { label: 'Ask CultBot', icon: 'smart_toy', action: 'open-cultbot' },
        { label: 'Get help on Discord', icon: 'discord', action: 'open-discord' },
      ]
    },
    {
      id: 'result-website-bug',
      question: 'Report a website bug',
      resultText: 'Thanks for flagging it — website bugs get fixed fastest when reported with a description and a screenshot, if possible.',
      actions: [
        { label: 'Report on Discord', icon: 'discord', action: 'open-discord' },
      ]
    },
    {
      id: 'result-fallback',
      question: 'Let\'s find you the right help',
      resultText: 'No worries — CultBot can dig into specifics, or the team on Discord can help directly.',
      actions: [
        { label: 'Ask CultBot', icon: 'smart_toy', action: 'open-cultbot' },
        { label: 'Join Discord', icon: 'discord', action: 'open-discord' },
      ]
    },
    {
      id: LOGIN_REQUIRED_ID,
      question: 'Log in to continue',
      resultText: 'This step needs to check your connected VRChat and Patreon accounts, so please log in first.',
      loginRequired: true,
    },
  ];

  currentNodeId = TROUBLESHOOT_ROOT_ID;
  private history: string[] = [];

  helpFabOpen = false;

  constructor(
    private dialog: MatDialog,
    public accountService: AccountService,
    public chatbotService: ChatbotService,
    private router: Router
  ) {}

  get currentTroubleshootNode(): TroubleshootNode {
    return this.findTroubleshootNode(this.currentNodeId) ?? this.troubleshootTree[0];
  }

  get isTroubleshootAtStart(): boolean {
    return this.history.length === 0;
  }

  get isLoggedIn(): boolean {
    return !!this.accountService.account.value;
  }

  get isVrchatConnected(): boolean {
    return !!this.accountService.account.value?.connections?.vrchat;
  }

  get isPatreonActive(): boolean {
    const patreon = this.accountService.account.value?.connections?.patreon;
    return !!patreon && patreon.subscriptionTier.id >= 0;
  }

  get patreonStatusLabel(): string {
    const patreon = this.accountService.account.value?.connections?.patreon;
    return patreon ? patreon.subscriptionTier.name : 'Not connected';
  }

  // Resolves resultText for the current node, computing it from live account
  // data when the node uses dynamicText instead of static copy.
  get currentResultText(): string {
    const node = this.currentTroubleshootNode;
    switch (node.dynamicText) {
      case 'perks-vrchat-missing':
        return 'Your perks aren\'t showing up because your VRChat account isn\'t connected to Jar.games yet. Connect it below and your perks will apply after the next processing cycle.';
      case 'perks-patreon-issue': {
        const patreon = this.accountService.account.value?.connections?.patreon;
        if (!patreon) {
          return 'Your perks aren\'t showing up because you haven\'t connected a Patreon account yet. Connect it below to link your membership.';
        }
        if (patreon.subscriptionTier.id === -2) {
          return 'Your perks aren\'t showing up because your last Patreon payment was declined. Update your payment method on Patreon, then reconnect below.';
        }
        return 'Your perks aren\'t showing up because the connected Patreon account (' + patreon.username + ') doesn\'t have an active membership to Jar. Double-check it\'s the account with your active pledge, reconnecting if needed.';
      }
      default:
        return node.resultText ?? '';
    }
  }

  private findTroubleshootNode(id: string): TroubleshootNode | undefined {
    return this.troubleshootTree.find(n => n.id === id);
  }

  // Follows autoCheck nodes (and gates requiresAccount nodes behind login)
  // until it lands on a node that should actually be shown to the user.
  private resolveTroubleshootNode(id: string): string {
    let node = this.findTroubleshootNode(id);
    let guard = 0;
    while (node && guard++ < this.troubleshootTree.length) {
      if (node.requiresAccount && !this.isLoggedIn) {
        return LOGIN_REQUIRED_ID;
      }
      if (!node.autoCheck) {
        return node.id;
      }
      id = this.evaluateTroubleshootAutoCheck(node.autoCheck);
      node = this.findTroubleshootNode(id);
    }
    return id;
  }

  private evaluateTroubleshootAutoCheck(check: TroubleshootAutoCheck): string {
    const connections = this.accountService.account.value?.connections;
    switch (check) {
      case 'vrchat-connected-check':
        return connections?.vrchat ? 'perks-check-patreon' : 'result-perks-no-vrchat';
      case 'patreon-active-check': {
        const patreon = connections?.patreon;
        if (!patreon || patreon.subscriptionTier.id < 0) return 'result-perks-patreon-issue';
        return 'perks-check-restart';
      }
    }
  }

  selectTroubleshootOption(option: TroubleshootOption) {
    this.history.push(this.currentNodeId);
    this.currentNodeId = this.resolveTroubleshootNode(option.next);
  }

  troubleshootBack() {
    const previous = this.history.pop();
    if (previous) {
      this.currentNodeId = previous;
    }
  }

  troubleshootRestart() {
    this.history = [];
    this.currentNodeId = TROUBLESHOOT_ROOT_ID;
  }

  isTroubleshootActionVisible(action: TroubleshootAction): boolean {
    const connections = this.accountService.account.value?.connections;
    switch (action.visibleWhen) {
      case 'vrchat-connected': return !!connections?.vrchat;
      case 'vrchat-disconnected': return !connections?.vrchat;
      case 'patreon-connected': return !!connections?.patreon;
      case 'patreon-disconnected': return !connections?.patreon;
      default: return true;
    }
  }

  runTroubleshootAction(action: TroubleshootActionId) {
    switch (action) {
      case 'connect-vrchat': return this.openConnectVRChatDialog();
      case 'disconnect-vrchat': return this.openDisconnectVRChatDialog();
      case 'connect-patreon': return this.openConnectPatreonDialog();
      case 'disconnect-patreon': return this.openDisconnectPatreonDialog();
      case 'submit-script': return this.openGuestSubmissionDialog();
      case 'open-discord': return this.openDiscord();
      case 'open-cultbot': return this.openCultBot();
    }
  }

  // Get Help FAB
  toggleHelpFab() {
    this.helpFabOpen = !this.helpFabOpen;
  }

  closeHelpFab() {
    this.helpFabOpen = false;
  }

  openDiscord() {
    window.open('https://discord.gg/cult-of-jar', '_blank');
    this.closeHelpFab();
  }

  openCultBot() {
    this.chatbotService.openChatbot();
    this.closeHelpFab();
  }

  scrollToFAQ() {
    const faqSection = document.getElementById('faq-section');
    if (faqSection) {
      faqSection.scrollIntoView({ behavior: 'smooth' });
    }
  }

  scrollToTroubleshoot() {
    const section = document.getElementById('troubleshoot-section');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  }

  // FAQ Action Methods
  openConnectVRChatDialog() {
    this.dialog.open(ConnectVRChatComponent);
  }

  openConnectPatreonDialog() {
    // Connecting needs a solved proof of work, which the Patreon page prepares
    this.router.navigate(['/patreon']);
  }

  openDisconnectVRChatDialog() {
    this.dialog.open(DisconnectVrchatComponent);
  }

  openDisconnectPatreonDialog() {
    this.dialog.open(DisconnectPatreonComponent);
  }

  openGuestSubmissionDialog() {
    this.dialog.open(GuestSubmissionComponent);
  }
}
